import { WorkDocsDocument, WorkDocsFolder } from 'src/services/WorkDocsService';
import { OrgChartEmployee } from '../types/orgChart.types';
import { documentMatchesEmployee } from './documentMatch';

/**
 * Result of splitting a set of selected employees into those whose required document
 * was found in a folder (`covered`) and those still missing it (`missing`). Both arrays
 * hold employee logins. Used by the Prior Cards, Annual Review, and Supplemental checks.
 */
export interface EmployeeCoverageResult {
  covered: string[];
  missing: string[];
}

/** A single person row for the "Included & Missing" coverage list: a login plus its coverage flag. */
export interface CoverageDisplayEntry {
  login: string;
  included: boolean;
}

/**
 * Flattens a coverage result into one display list for the "Included & Missing" row:
 * INCLUDED people first, then MISSING people. Order within each bucket preserves the
 * original `covered`/`missing` order (i.e. selectedEmployees order), so no comparator is
 * needed — the concatenation order does the sorting.
 */
export function buildCoverageDisplayList(result: EmployeeCoverageResult): CoverageDisplayEntry[] {
  return [...result.covered.map((login) => ({ login, included: true })), ...result.missing.map((login) => ({ login, included: false }))];
}

/**
 * Finds a direct child folder by exact name (case-sensitive — the fixed subfolder names
 * are created verbatim by the provisioning flow). Returns the folder id, or `undefined` if
 * no child with that name exists (the idiomatic "absent" value the readiness helpers accept).
 */
export function findSubfolderIdByName(folders: WorkDocsFolder[], name: string): string | undefined {
  const match = folders.find((folder) => folder.name === name);
  return match ? match.id : undefined;
}

/**
 * Counts documents whose file name ends in `.csv` (case-insensitive). Used by the
 * "Manager Hub Ratings" check, which expects exactly one CSV.
 */
export function countCsvFiles(documents: WorkDocsDocument[]): number {
  return documents.filter((doc) => doc.name.toLowerCase().endsWith('.csv')).length;
}

/**
 * True if any document is the leadership-principles export: a file name (case-insensitive)
 * that starts with `team-lps` and ends in `.csv` (e.g. `team-lps.csv`, `Team-LPs_2026.csv`) —
 * the naming the Harmony `forte-team-lp-export` script produces. Used by the "Annual Review
 * Feedback" check, which only needs one such file present — no per-employee matching. `.trim()`
 * guards against a leading space in the stored file name.
 */
export function hasTeamLpsCsvFile(documents: WorkDocsDocument[]): boolean {
  return documents.some((doc) => {
    const name = doc.name.trim().toLowerCase();
    return name.startsWith('team-lps') && name.endsWith('.csv');
  });
}

/**
 * True if any document is the Manager Hub ratings export: a file name (case-insensitive) that
 * starts with `manager-hub` and ends in `.xlsx` (e.g. `manager-hub.xlsx`, `Manager-Hub_2026.xlsx`).
 * Used by the "Manager Hub Exports" check, which only needs one such file present — no per-employee
 * matching. `.trim()` guards against a leading space in the stored file name.
 */
export function hasManagerHubXlsxFile(documents: WorkDocsDocument[]): boolean {
  return documents.some((doc) => {
    const name = doc.name.trim().toLowerCase();
    return name.startsWith('manager-hub') && name.endsWith('.xlsx');
  });
}

/**
 * Splits `employees` by whether at least one document in `documents` matches them
 * (via {@link documentMatchesEmployee}). Returns covered/missing logins. Pure —
 * callers supply the already-listed folder documents.
 */
export function evaluateEmployeeCoverage(documents: WorkDocsDocument[], employees: OrgChartEmployee[]): EmployeeCoverageResult {
  const covered: string[] = [];
  const missing: string[] = [];

  for (const employee of employees) {
    const hasDoc = documents.some((doc) => documentMatchesEmployee(doc.name, employee));
    if (hasDoc) {
      covered.push(employee.login);
    } else {
      missing.push(employee.login);
    }
  }

  return { covered, missing };
}
